// Button — primary action button per COMPONENTS §Button.
//
// 4 variants (primary / secondary / danger / ghost), default size only.
// Hit target ≥24×24 CSS px via padding (WCAG 2.2 SC 2.5.8).
// Focus ring via --color-primary-ring (Layer 1, 22% opacity).
// Loading state: <Spinner> inside; original label text retained for layout
// stability; aria-disabled + aria-busy="true" while loading.
//
// loading vs disabled are independent props — loading = in-flight,
// disabled = preconditions unmet.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Spinner } from './Spinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link' | 'stage';

// Semantic color for the `link` variant (ignored by the filled variants). A link-
// styled ACTION is colored by its FUNCTION, NEVER by brand — most are 'info', an
// exit/caution link can be 'danger', a low-stakes one 'neutral'. Default 'info'.
export type ButtonTone = 'info' | 'danger' | 'neutral';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  /** `link`-variant semantic color (see ButtonTone). Default 'info'. */
  tone?: ButtonTone;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  children?: ReactNode;
}

// Shared by every variant. The button "shape" (padding, min-height, centered,
// rounded-md) is NOT here — it lives in buttonShape, applied only to the four
// solid/outline variants. The `link` variant opts out of the shape so it renders
// as compact inline text (a link-styled action button).
// `aria-disabled` alone (a control kept pressable and focusable while announced inactive — the phone
// drawer's "Clear all": a disabled button would drop focus out of the dialog) gets the dimmed look too.
const baseClass =
  'inline-flex items-center gap-2 text-sm font-medium transition-colors outline-none ' +
  'focus-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50';

const buttonShape = 'justify-center px-4 py-2 min-h-[2.5rem] rounded-md whitespace-nowrap';

const variantClass: Record<ButtonVariant, string> = {
  primary:
    `${buttonShape} bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)] hover:brightness-110 active:brightness-95`,
  secondary:
    `${buttonShape} bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] border border-[var(--color-border)] ` +
    'hover:bg-[var(--color-bg-hover)] active:bg-[var(--color-border)]',
  danger:
    `${buttonShape} bg-[var(--color-danger)] text-[var(--color-on-danger)] hover:brightness-110 active:brightness-95`,
  ghost:
    `${buttonShape} bg-transparent text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] active:bg-[var(--color-border)]`,
  // On the STAGE ground (dark in both themes) — the stage's own ink, never the
  // theme's text tokens (they vanish there in light mode).
  stage:
    `${buttonShape} border border-transparent bg-[var(--color-bg-stage)]/70 text-[var(--color-on-stage)] hover:bg-[var(--color-bg-stage)]/90`,
  // Link — text styled as a semantic link (compact; no button padding/fill). For
  // low-weight inline ACTIONS (Clear filters, Download errors, Edit, Sign out). Color
  // comes from `tone` (default info), NEVER brand. Focus ring inherited from
  // baseClass; WCAG 2.5.8 inline-target exception applies (it reads as a link).
  link:
    'rounded-sm underline-offset-2 hover:underline active:opacity-80',
};

// `link`-variant color by semantic tone (DESIGN-TOKENS §Color — links are semantic,
// not brand; brand is reserved for filled primary actions). Default 'info'.
const linkToneClass: Record<ButtonTone, string> = {
  info: 'text-[var(--color-info)]',
  danger: 'text-[var(--color-danger)]',
  neutral: 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
};

// forwardRef so it can be a React Aria interaction trigger (Menu/Popover via
// <Pressable>, which needs the underlying DOM ref).
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', tone = 'info', loading = false, disabled = false, type = 'button', className = '', children, ...props },
  ref,
) {
  const isInactive = disabled || loading;
  const toneClass = variant === 'link' ? ` ${linkToneClass[tone]}` : '';
  return (
    <button
      ref={ref}
      type={type}
      disabled={isInactive}
      aria-disabled={isInactive || undefined}
      aria-busy={loading || undefined}
      className={`${baseClass} ${variantClass[variant]}${toneClass} ${className}`}
      {...props}
    >
      {loading && <Spinner size={16} />}
      {children}
    </button>
  );
});
